import { useEffect, useMemo, useState } from "react";
import {
  FlightSelectors,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { I18nMarkup } from "@hopper-b2b/i18n";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import { MobileFlightDetailsModal } from "@hopper-b2b/ui";
import {
  getIsMixedClass,
  getPlusDays,
  getSliceIndex,
  useEnableDarkMode,
} from "@hopper-b2b/utilities";
import clsx from "clsx";
import dayjs from "dayjs";

import AirplaneIcon from "../../../../../../assets/client/airplane.svg";
import AirplaneWhiteIcon from "../../../../../../assets/client/darkMode/airplane-white.svg";
import LocationMarkerWhite from "../../../../../../assets/client/darkMode/location-marker-white.svg";
import LocationMarker from "../../../../../../assets/client/location-marker.svg";

interface ItineraryDetailsModal {
  openModal: "departure" | "return" | null;
  onClose: () => void;
}

export const ItineraryDetailsModal = ({
  openModal,
  onClose,
}: ItineraryDetailsModal) => {
  const isDeparture = openModal === "departure";

  const darkModeAllowed = useEnableDarkMode();
  const isDarkMode = darkModeAllowed && getDarkModePreferred();
  const airports = useSelector(FlightSelectors.getAirportMap);
  const tripDetails = useSelector(FlightSelectors.getSelectedTripDetailsParent);
  const fareDetails = useSelector(FlightSelectors.getSelectedFareDetails);
  const sliceIndex = tripDetails ? getSliceIndex(isDeparture, tripDetails) : 0;
  const tripSlice = tripDetails?.slices[sliceIndex];
  const [isMixedCabinClass, setIsMixedCabinClass] = useState(false);

  useEffect(() => {
    if (fareDetails && openModal) {
      isDeparture
        ? setIsMixedCabinClass(getIsMixedClass(fareDetails.slices[0]))
        : setIsMixedCabinClass(getIsMixedClass(fareDetails.slices[1]));
    }
  }, [fareDetails, openModal, isDeparture]);

  const assets = useMemo(() => {
    return isDarkMode
      ? {
          airplane: AirplaneWhiteIcon,
          locationMarker: LocationMarkerWhite,
        }
      : {
          airplane: AirplaneIcon,
          locationMarker: LocationMarker,
        };
  }, [isDarkMode]);

  return (
    <MobileFlightDetailsModal
      departure={isDeparture}
      title={
        <I18nMarkup
          tKey={
            isDeparture
              ? "flightShopReview.outboundCardHeader"
              : "flightShopReview.returnCardHeader"
          }
          replacements={{
            location: tripSlice?.destinationCode,
            date: dayjs(tripSlice?.departureTime).format("ddd, MMM D"),
          }}
        />
      }
      plusDays={getPlusDays(tripSlice)}
      isMixedCabinClass={isMixedCabinClass}
      className={clsx("b2b", "b2b-secondary", "mobile-itinerary-details-modal")}
      airports={airports}
      onClose={onClose}
      tripDetails={tripDetails}
      fareDetails={fareDetails}
      assets={assets}
      openModal={!!openModal}
    />
  );
};
